// One reminder, drawn once — and the three urgency groups around it.
//
// Round 38, Nathan's word after clicking through: the round-37 feed worked but
// read as a plain list of links. Zanetto Tools' reminders page is the model
// (client/src/pages/reminders/Reminders.jsx) — a box per item, colour-coded by
// kind, the WHOLE box clickable, under section headings that say how urgent
// the group is.
//
// WHY THIS IS A COMPONENT RATHER THAN MARKUP ON TWO PAGES. Home's summary and
// the Notifications page were byte-for-byte duplicates of the same list, which
// is the drift round 37 spent a whole engine (`lib/reminderData.ts`, one engine
// two consumers) avoiding on the server — and then reintroduced in the
// rendering. Everything a reader could compare between the two screens (the
// icon, the colour, the wording, the date, where the click goes) is decided
// here, once.
//
// The two screens differ only in CONTAINER, which is what `variant` is for:
// the page has room for cards, Home's panel does not — cards inside a panel is
// card-in-card, the round-35 bug shape. A row keeps every rule the card has
// and drops the chrome the panel already supplies.
import { Link } from 'react-router-dom';
import type { ReactNode } from 'react';
import type { Reminder, ReminderKind } from '../homeApi';
import { formatDate } from '../lib/dates';

export const REMINDER_KIND_LABEL: Record<ReminderKind, string> = {
  registration: 'Registrations',
  service: 'Services',
  quote: 'Quotes',
  record: 'Tickets',
  insurance: 'Insurances',
  document: 'Documents',
  timesheet: 'Timesheets',
  birthday: 'Birthdays',
  anniversary: 'Anniversaries',
};

/** In the engine's own order: the ones with consequences first. */
export const REMINDER_KINDS: ReminderKind[] = [
  'registration',
  'service',
  'quote',
  'record',
  'insurance',
  'document',
  'timesheet',
  'birthday',
  'anniversary',
];

const svg = (path: ReactNode) => (
  <svg
    className="h-5 w-5"
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
    strokeWidth={1.8}
    aria-hidden="true"
  >
    {path}
  </svg>
);

/**
 * Heroicons outline, the same family the sidebar draws (navConfig's `icon`).
 * Four of the six are ZT's own paths, brought over with the kinds they name.
 */
const ICON: Record<ReminderKind, ReactNode> = {
  // A plate on a card — the rego.
  registration: svg(
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M10 6H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V8a2 2 0 00-2-2h-5m-4 0a2 2 0 104 0m-4 0a2 2 0 114 0m-5 8a2 2 0 100-4 2 2 0 000 4zm0 0c1.306 0 2.417.835 2.83 2M9 14a3.001 3.001 0 00-2.83 2M15 11h3m-3 4h2"
    />
  ),
  // Wrench — a machine due for servicing.
  service: svg(
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M11.42 15.17L17.25 21A2.652 2.652 0 0021 17.25l-5.877-5.877M11.42 15.17l2.496-3.03c.317-.384.74-.626 1.208-.766M11.42 15.17l-4.655 5.653a2.548 2.548 0 11-3.586-3.586l6.837-5.63m5.108-.233c.55-.164 1.163-.188 1.743-.14a4.5 4.5 0 004.486-6.336l-3.276 3.277a3.004 3.004 0 01-2.25-2.25l3.276-3.276a4.5 4.5 0 00-6.336 4.486c.091 1.076-.071 2.264-.904 2.95l-.102.085"
    />
  ),
  // A calculator: a priced quote whose client deadline is near (round 43).
  quote: svg(
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z"
    />
  ),
  // Identity card. ONE icon for competencies and inductions both: they are one
  // kind in the feed and one section on a person's page, and giving them two
  // icons would invite the reader to tell apart a distinction the filter chip
  // already makes when they want it.
  record: svg(
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M10 6H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V8a2 2 0 00-2-2h-5m-4 0a2 2 0 104 0m-4 0a2 2 0 114 0M9 14a3 3 0 100-6 3 3 0 000 6zm0 0c-1.657 0-3 1.007-3 2.25V17h6v-.75C12 15.007 10.657 14 9 14zm6-3h3m-3 3h3"
    />
  ),
  // Shield with a tick — cover in place. Heroicons' shield-check, the same
  // outline family as the rest.
  insurance: svg(
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z"
    />
  ),
  document: svg(
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
    />
  ),
  // A clock: a week of somebody's hours (round 44).
  timesheet: svg(
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 2m6-2a9 9 0 11-18 0 9 9 0 0118 0z" />
  ),
  birthday: svg(
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M12 8v13m0-13V6a2 2 0 112 2h-2zm0 0V5.5A2.5 2.5 0 109.5 8H12zm-7 4h14M5 12a2 2 0 110-4h14a2 2 0 110 4M5 12v7a2 2 0 002 2h10a2 2 0 002-2v-7"
    />
  ),
  anniversary: svg(
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M12 15a4 4 0 100-8 4 4 0 000 8zm0 0v6l-3-2-3 2 1.5-6M12 21l3-2 3 2-1.5-6"
    />
  ),
};

/**
 * THE COLOUR SAYS WHICH KIND IT IS, not how urgent it is. Urgency is already
 * said twice — by the group the item is in and by the wording of its detail
 * line — and a third telling in colour would leave nothing to distinguish a
 * lapsed licence from a birthday at a glance. ZT's rule, and its exception
 * comes with it: the kinds with consequences escalate to red once they are
 * overdue, because "expired" is not the same statement as "expiring".
 *
 * The hues are Dayboard's, not ZT's, and the difference is the brand rules:
 * ZT paints services orange and quotes sky, and here ORANGE IS THE BRAND (an
 * orange chip reads as a selected state — jobColour.ts learned this) and BLUE
 * IS INFORMATION. So services take teal, the hue round 34 added for movement,
 * and the two celebrations take purple and emerald, which nothing in the
 * semantic set is using as a status.
 */
function circleTone(item: Reminder): string {
  if (item.overdue) return 'bg-red-100 text-red-700';
  switch (item.kind) {
    case 'registration':
    case 'record':
      return 'bg-amber-100 text-amber-800';
    case 'service':
      return 'bg-teal-100 text-teal-800';
    // Lime (round 43): the last hue nothing in the set uses as a status,
    // clear of the brand's orange, information's blue and insurance's
    // indigo. A missed submission escalates to red like the other kinds
    // with consequences; the `if` above catches it first.
    case 'quote':
      return 'bg-lime-100 text-lime-800';
    // Indigo: a hue nothing in the semantic set is using as a status, and
    // far enough from banner-info's blue that it cannot read as one
    // (round 41). Escalates to red once overdue like the other
    // consequence kinds — the `if` above catches it first.
    case 'insurance':
      return 'bg-indigo-100 text-indigo-800';
    case 'document':
      return 'bg-gray-100 text-gray-700';
    // Cyan (round 44): the hue left that is neither the brand's orange,
    // information's blue nor any status in the semantic set, and far enough
    // from the services' teal to tell apart. A week not handed in escalates
    // to red once overdue; the if above catches it first.
    case 'timesheet':
      return 'bg-cyan-100 text-cyan-800';
    case 'birthday':
      return 'bg-purple-100 text-purple-700';
    case 'anniversary':
      return 'bg-emerald-100 text-emerald-700';
  }
}

/**
 * `date === null` is a meter-driven service — its `days` is a sort key and
 * nothing else, so it must not be read as "today" (the server's own grouping
 * makes the same check, which is why one of these never lands in the Today
 * group in the first place).
 */
function detailTone(item: Reminder): string {
  if (item.overdue) return 'text-red-700';
  if (item.date !== null && item.days === 0) return 'text-amber-800';
  return 'text-gray-600';
}

export type ReminderVariant = 'card' | 'row';

/**
 * One reminder. The whole thing is the link — a title-only anchor makes the
 * reader aim at four words when the box in front of them is the target
 * (round 35's Lately rule, and the reason RowCard and LinkedRow exist).
 */
export function ReminderCard({
  item,
  variant = 'card',
}: {
  item: Reminder;
  variant?: ReminderVariant;
}) {
  const chrome =
    variant === 'card'
      ? 'card transition-shadow hover:shadow-md focus-visible:shadow-md'
      : 'rounded-md transition-colors hover:bg-brand-50/40 active:bg-brand-50';

  return (
    <Link
      to={item.link}
      className={`flex min-h-11 items-center gap-3 px-4 py-3 no-underline focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-600 ${chrome}`}
    >
      <span
        className={`flex h-9 w-9 flex-none items-center justify-center rounded-full ${circleTone(item)}`}
      >
        {ICON[item.kind]}
      </span>

      <span className="min-w-0 flex-1">
        <span className="block truncate text-sm font-semibold text-gray-900">{item.label}</span>
        <span className={`mt-0.5 block text-sm ${detailTone(item)}`}>{item.detail}</span>
      </span>

      {/* Nothing at all for a meter-driven service: it has no date, and an
          em-dash in the column would be the page inventing one. */}
      {item.date && (
        <span className="flex-none whitespace-nowrap text-xs text-gray-500">
          {formatDate(item.date)}
        </span>
      )}
    </Link>
  );
}

/**
 * One urgency group. Empty ones draw NOTHING — a permanent "Overdue 0" is a
 * heading people stop reading, the same reasoning that hides the badge at zero.
 */
function Group({
  title,
  items,
  tone,
  variant,
}: {
  title: string;
  items: Reminder[];
  tone: 'bad' | 'neutral';
  variant: ReminderVariant;
}) {
  if (items.length === 0) return null;

  return (
    <section>
      <h2
        className={`!mb-2 text-xs font-semibold uppercase tracking-wide ${
          tone === 'bad' ? 'text-red-700' : 'text-gray-500'
        }`}
      >
        {title}
        <span className="ml-1.5 font-normal text-gray-400">{items.length}</span>
      </h2>
      <ul className={`m-0 list-none p-0 ${variant === 'card' ? 'space-y-2' : 'space-y-1'}`}>
        {items.map((item) => (
          // Keyed on kind AND id: one person can be a birthday and an
          // anniversary on the same day, and one machine a rego and a service.
          <li key={`${item.kind}-${item.entityId}`}>
            <ReminderCard item={item} variant={variant} />
          </li>
        ))}
      </ul>
    </section>
  );
}

export interface ReminderGroupsData {
  overdue: Reminder[];
  today: Reminder[];
  upcoming: Reminder[];
}

/**
 * The three groups, in the one order they are ever drawn in.
 *
 * `horizon` must be the WIDER of the two lead windows, or the heading
 * contradicts the rows beneath it: a licence expiring in six weeks appears in
 * this group and "Next 7 days" over it would be the page arguing with itself
 * (round 27's rule).
 */
export function ReminderGroups({
  groups,
  horizon,
  variant = 'card',
}: {
  groups: ReminderGroupsData;
  horizon: number;
  variant?: ReminderVariant;
}) {
  return (
    <div className={variant === 'card' ? 'space-y-6' : 'space-y-4'}>
      <Group title="Overdue" items={groups.overdue} tone="bad" variant={variant} />
      <Group title="Today" items={groups.today} tone="neutral" variant={variant} />
      <Group
        title={`Next ${horizon} days`}
        items={groups.upcoming}
        tone="neutral"
        variant={variant}
      />
    </div>
  );
}
